import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable client-side pagination footer.
 *
 * Props:
 *   total       — total rows across ALL pages
 *   page        — current 1-indexed page
 *   pageSize    — rows-per-page (or 0 == "All")
 *   pageSizes   — options for the size selector (default [100, 250, 500, "All"])
 *   entityLabel — noun used in "1-100 of 236 invoices"
 *   onPageChange, onPageSizeChange — controlled callbacks
 */
export default function Pagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  entityLabel = "rows",
  pageSizes = [100, 250, 500, "All"],
}) {
  const effective = pageSize === 0 || pageSize === "All" ? total : pageSize;
  const pageCount = Math.max(1, Math.ceil(total / (effective || 1)));
  const from = total === 0 ? 0 : (page - 1) * effective + 1;
  const to = Math.min(page * effective, total);

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-line bg-card"
      data-testid="pagination"
    >
      <div className="text-xs text-ink-2 tabular">
        {total === 0 ? (
          <>No {entityLabel} to show</>
        ) : (
          <>
            Showing <span className="text-ink font-semibold">{from}-{to}</span> of{" "}
            <span className="text-ink font-semibold">{total}</span> {entityLabel}
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-ink-2 uppercase tracking-wider">Per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(v === "All" ? "All" : Number(v))}
          >
            <SelectTrigger
              className="rounded-sm h-8 w-24"
              data-testid="pagination-page-size"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((n) => (
                <SelectItem key={String(n)} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm bg-white text-ink hover:bg-[color:var(--bone)] hover:text-ink h-8 px-2"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            data-testid="pagination-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs tabular px-2">
            Page <span className="font-semibold">{page}</span> of{" "}
            <span className="font-semibold">{pageCount}</span>
          </span>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm bg-white text-ink hover:bg-[color:var(--bone)] hover:text-ink h-8 px-2"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            data-testid="pagination-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
